/**
 * File: ga4Utils.ts
 * Path: apps/admin/src/features/dashboard/utils/ga4Utils.ts
 * Description: Turns a GA4 report (one row per day) into the three headline numbers and the traffic trend.
 */

export interface Ga4ReportRow {
  dimensionValues: string[];
  metricValues: string[];
}

export interface Ga4ReportResponse {
  rows: Ga4ReportRow[];
  rowCount: number;
}

export interface ChartDataPoint {
  label: string;
  value: number;
}

export interface TrafficMetrics {
  activeUsers: number;
  sessions: number;
  engagementRate: number;
}

export const EMPTY_TRAFFIC_METRICS: TrafficMetrics = { activeUsers: 0, sessions: 0, engagementRate: 0 };

/** The default range: the last 30 days up to today. */
export const lastThirtyDays = (now: number = Date.now()) => ({
  startDate: new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  endDate: new Date(now).toISOString().split("T")[0],
});

export const summarizeTraffic = (
  report: Pick<Ga4ReportResponse, "rows">,
): { metrics: TrafficMetrics; trend: ChartDataPoint[] } => {
  const rows = report.rows || [];

  let totalUsers = 0;
  let totalSessions = 0;
  let totalEngagementRateSum = 0;

  const trend: ChartDataPoint[] = rows
    .map((row) => {
      const rawDate = row.dimensionValues[0] || "";
      const users = parseInt(row.metricValues[0] || "0", 10);
      const sessions = parseInt(row.metricValues[1] || "0", 10);
      const rate = parseFloat(row.metricValues[2] || "0");

      totalUsers += users;
      totalSessions += sessions;
      totalEngagementRateSum += rate;

      const label = rawDate.length === 8 ? `${rawDate.substring(4, 6)}/${rawDate.substring(6, 8)}` : rawDate;

      return { label, value: users };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const rowCount = rows.length || 1;

  return {
    metrics: { activeUsers: totalUsers, sessions: totalSessions, engagementRate: totalEngagementRateSum / rowCount },
    trend,
  };
};
